import { DatePicker, Input, Modal, Select } from 'antd'
import PropTypes from "prop-types";
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify';
import { saveVictime, updateVictime } from '../../../../../hooks/useHooks';
import { getNames } from 'country-list';
import TextArea from 'antd/es/input/TextArea';
import dayjs from 'dayjs';
import axiosInstance from '../../../../../variables/api/axiosInstance';
import { CarOutlined } from '@ant-design/icons';

/**
 * Construit les options de véhicules disponibles pour l'affectation d'une victime.
 * Peut être importée et réutilisée par le composant parent (VictimesSection, etc.)
 */
export const buildVehicleOptions = (contrat, adversaires = [], engin = null) => {
    const opts = [];

    // Véhicule assuré — toujours proposé pour un dossier sinistre (le sinistre porte sur
    // un véhicule assuré). L'immatriculation provient de l'engin du sinistre (ou du contrat).
    if (contrat || engin) {
        const immat = engin?.immatriculation || contrat?.immatriculation;
        const label = immat
            ? `🚗 ${immat} – Véhicule de l'assuré`
            : "🚗 Véhicule de l'assuré";
        opts.push({ value: 'ASSURE', label });
    }

    adversaires.forEach((adv, i) => {
        if (adv.engin) {
            const immat = adv.engin?.immatriculation || `Adversaire ${i + 1}`;
            const nom = adv.personne?.nom
                ? ` – ${adv.personne?.nom} ${adv.personne?.prenom || ''}`
                : '';
            opts.push({ value: `ADV_${adv.codeId || i}`, label: `${immat}${nom}` });
        }
    });

    opts.push({ value: 'AUCUN', label: '🚶 Aucun (piéton / hors véhicule)' });

    return opts;
};

const AddVictime = (props) => {
    const [victime, setVictime] = useState({});
    const [isVictimeModalVisible, setIsVictimeModalVisible] = useState(false);
    const [updateVictimeLoading, setUpdateVictimeLoading] = useState(false);
    const [pays, setPays] = useState([]);
    const [professions, setProfessions] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [toUpdate, setToUpdate] = useState(false);

    /* ── Options de véhicules : prop directe ou construction à la volée ── */
    const vehicleOptions = props.vehicleOptions
        ?? buildVehicleOptions(
            props.contrat ?? props.sinistre?.contrat,
            props.adversaires,
            props.sinistre?.engin
        );

    const handleChange = (e, nom) => {
        if (nom) {
            setVictime({ ...victime, [nom]: e });
        } else {
            const { name, value } = e.target;
            setVictime({ ...victime, [name]: value });
        }
    };

    useEffect(() => {
        if (props.edit) {
            setVictime({
                ...props?.victime,
                nom: props?.victime?.personne?.nom || "",
                prenom: props?.victime?.personne?.prenom || "",
                sexe: props?.victime?.personne?.genre || "",
                lieuNaissance: props?.victime?.personne?.lieuNaissance || "",
                adresse: props?.victime?.personne?.adresse || "",
                telephone: (props?.victime?.personne?.contact && props?.victime?.personne?.contact[0]) || "",
                profession: props?.victime?.personne?.profession || "",
                dateConsolidation: props?.victime?.dateConsolidation ? dayjs(props?.victime?.dateConsolidation) : null,
                dateNaissance: props?.victime?.personne?.dateNaissance ? dayjs(props?.victime?.personne?.dateNaissance) : null,
                dateDernierSalaire: props?.victime?.dateDernierSalaire ? dayjs(props?.victime?.dateDernierSalaire) : null,
                casVictime: props?.victime?.blesseSinistre ? "BLESSE" : props?.victime?.decesSinistre ? "DECEDE" : props?.victime?.objet ? "AUTRE" : "",
                graviteBlessure: props?.victime?.graviteBlessure || "",
                perceptionSalairePendantTraitement: props?.victime?.perceptionSalairePendantTraitement ? "OUI" : "NON",
                typeOrphelin: props?.victime?.typeOrphelin || "",
                vehiculeAffecte: props?.victime?.vehiculeHote || "",
            });
        }
        setEditMode(props?.edit);
    }, [props.victime, props?.edit]);

    useEffect(() => {
        axiosInstance
            .get(`/api/parametre/type/PROFESSION`)
            .then((res) => {
                const options = (res.data || [])
                    .map((item) => ({
                        value: item?.libelleParametre || item?.codeId || "",
                        label: item?.libelleParametre || item?.description || "",
                    }))
                    .filter((option) => option.value && option.label);
                setProfessions(options);
            })
            .catch((err) => console.log(err));

        setIsVictimeModalVisible(props.open);
    }, [props.open]);

    useEffect(() => {
        if (!isVictimeModalVisible) {
            props.setOpen(false);
        }
    }, [isVictimeModalVisible]);

    useEffect(() => {
        const countries = getNames();
        setPays(countries);
    }, []);

    const handleSaveVictime = async () => {
        try {
            if (!victime?.casVictime) {
                toast.error("Veuillez choisir le cas de la victime", { position: "top-right", autoClose: 5000 });
                return;
            }
            if (!victime?.nom) {
                toast.error("Veuillez saisir le nom de la victime", { position: "top-right", autoClose: 5000 });
                return;
            }
            if (!victime?.prenom) {
                toast.error("Veuillez saisir le prenom de la victime", { position: "top-right", autoClose: 5000 });
                return;
            }

            victime.vehiculeHote = victime.vehiculeAffecte;

            if (victime?.dateConsolidation) victime.dateConsolidation = victime.dateConsolidation.format('YYYY-MM-DD');
            if (victime?.dateNaissance) victime.dateNaissance = victime.dateNaissance.format('YYYY-MM-DD');
            if (victime?.dateDernierSalaire) victime.dateDernierSalaire = victime.dateDernierSalaire.format('YYYY-MM-DD');

            victime.sinistre = props?.sinistre?.codeId;

            if (victime?.revenuProfessionelMensuelAvantAccident)
                victime.revenuProfessionelMensuelAvantAccident = parseFloat(victime.revenuProfessionelMensuelAvantAccident);
            if (victime?.perteRevenuProfessionelMensuelApresAccident)
                victime.perteRevenuProfessionelMensuelApresAccident = parseFloat(victime.perteRevenuProfessionelMensuelApresAccident);
            if (victime?.montantBourseMensuelle)
                victime.montantBourseMensuelle = parseFloat(victime.montantBourseMensuelle);

            setUpdateVictimeLoading(true);

            const response = editMode ? await updateVictime(victime) : await saveVictime(victime);

            setIsVictimeModalVisible(false);
            toast.success('Victime enregistrée avec succès!', { position: "top-right", autoClose: 5000 });

            if (typeof props.onSaved === 'function') {
                try { props.onSaved({ action: editMode ? 'update' : 'create', data: response?.data }); }
                catch (e) { /* noop */ }
            } else {
                window.location.reload();
            }
        } catch (error) {
            toast.error('Une erreur est survenue', { position: "top-right", autoClose: 5000 });
        } finally {
            setUpdateVictimeLoading(false);
        }
    };

    const handleNewVictimeClose = () => {
        setVictime("");
        setUpdateVictimeLoading(false);
        props.setOpen(false);
        setEditMode(false);
        setIsVictimeModalVisible(false);
    };

    const labelStyle = { margin: "10px 0 10px 0", width: "100%", color: null };
    const fieldContainerStyle = { display: "flex", flexDirection: "column" };
    const gridContainerStyle = {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
        gap: 16,
        alignItems: "start",
    };

    return (
        <Modal
            title={editMode ? "Modifier une Victime" : "Enregistrer une nouvelle Victime"}
            open={isVictimeModalVisible}
            confirmLoading={updateVictimeLoading}
            loading={updateVictimeLoading}
            onOk={handleSaveVictime}
            onCancel={handleNewVictimeClose}
            afterClose={handleNewVictimeClose}
            okText="Sauvegarder"
            cancelText="Annuler"
            width={"1200px"}
            zIndex={2000}
            okButtonProps={{ style: { background: '#242166' } }}
        >
            <div style={gridContainerStyle}>

                {/* Cas de la victime */}
                <div style={fieldContainerStyle}>
                    <label htmlFor="casVictime" style={labelStyle}>Cas de la victime *</label>
                    <Select
                        name="casVictime"
                        value={victime?.casVictime || ""}
                        onChange={(event) => {
                            // Sous-compteurs DÉCLARÉS par type (souvent 0 : la déclaration V2 ne
                            // saisit que le total « Nombre de victimes », pas la ventilation).
                            let nb = (props?.sinistre?.nombreBlesseLeger || 0) + (props?.sinistre?.nombreBlesseMoyen || 0) + (props?.sinistre?.nombreBlesseGrave || 0);
                            let ndc = (props?.sinistre?.nombreDeces || 0);
                            // Compteurs ENREGISTRÉS (hors partie adverse).
                            let ntvb = props?.sinistre?.victimes?.filter((v) => v.blesseSinistre && !v.adversaire)?.length || 0;
                            let ntd = props?.sinistre?.victimes?.filter((v) => v.decesSinistre && !v.adversaire)?.length || 0;
                            let ntTotal = props?.sinistre?.victimes?.filter((v) => !v.adversaire)?.length || 0;
                            // Plafond GLOBAL = nombre de victimes DÉCLARÉ (même règle que l'onglet
                            // Victimes) : sinistre MATÉRIEL → nombreVictimes saisi ; sinon (corporel /
                            // mixte) → somme des blessés + décès déclarés. Sans ça, un corporel
                            // requalifié (ex. 6 blessés graves) restait plafonné à l'ancien
                            // nombreVictimes (3) → impossible d'ajouter la 4ᵉ victime.
                            const estMateriel = props?.sinistre?.typeSinistre === "MATERIEL";
                            let nv = estMateriel ? (props?.sinistre?.nombreVictimes || 0) : (nb + ndc);
                            // On ne borne par le sous-compteur blessés/décès QUE s'il a été
                            // réellement déclaré (> 0). Sinon (ex. sinistre MATÉRIEL où seul le
                            // total est déclaré), on n'empêche plus la saisie et on s'appuie sur
                            // le plafond GLOBAL = nombre de victimes déclaré.
                            if (event === "BLESSE" && nb > 0 && ntvb >= nb) {
                                setToUpdate(true);
                                toast.warning("Le nombre de victimes blessées ne peut pas dépasser le nombre de victimes blessées dans l'accident.");
                            } else if (event === "DECEDE" && ndc > 0 && ntd >= ndc) {
                                setToUpdate(true);
                                toast.warning("Le nombre de victimes décédées ne peut pas dépasser le nombre de victimes décédées dans l'accident.");
                            } else if (nv > 0 && ntTotal >= nv) {
                                setToUpdate(true);
                                toast.warning(`Le nombre de victimes ne peut pas dépasser le nombre de victimes déclaré (${nv}).`);
                            } else {
                                setToUpdate(false);
                                handleChange(event, "casVictime");
                            }
                        }}
                        options={[
                            { value: "BLESSE", label: "Victime Blessée" },
                            { value: "DECEDE", label: "Victime Décédée" },
                            { value: "AUTRE", label: "Autre (dégât matériel / sans blessure)" },
                        ]}
                        style={{ width: "100%" }}
                    />
                </div>

                {/* Nom */}
                <div style={fieldContainerStyle}>
                    <label htmlFor="nom" style={labelStyle}>Nom de la victime *</label>
                    <Input name="nom" value={victime?.nom || ""} onChange={handleChange} placeholder="Nom de la victime" disabled={toUpdate} />
                </div>

                {/* Prénom */}
                <div style={fieldContainerStyle}>
                    <label htmlFor="prenom" style={labelStyle}>Prénoms de la victime *</label>
                    <Input name="prenom" value={victime?.prenom || ""} onChange={handleChange} placeholder="Prénoms de la victime" disabled={toUpdate} />
                </div>

                {/* Sexe */}
                <div style={fieldContainerStyle}>
                    <label htmlFor="sexe" style={labelStyle}>Sexe de la victime</label>
                    <Select
                        name="sexe"
                        value={victime?.sexe || ""}
                        onChange={(e) => handleChange(e, "sexe")}
                        options={[{ value: "M", label: "MASCULIN" }, { value: "F", label: "FEMININ" }]}
                        style={{ width: "100%" }}
                        disabled={toUpdate}
                    />
                </div>

                {/* Date de naissance */}
                <div style={fieldContainerStyle}>
                    <label htmlFor="dateNaissance" style={labelStyle}>Date de naissance de la victime</label>
                    <DatePicker
                        name="dateNaissance"
                        placeholder="Date de naissance de la victime"
                        value={victime?.dateNaissance || ""}
                        onChange={(e) => handleChange(e, "dateNaissance")}
                        style={{ width: "100%" }}
                        disabledDate={(current) => current && current > dayjs().endOf('day')}
                        disabled={toUpdate}
                    />
                </div>

                {/* Lieu de naissance */}
                <div style={fieldContainerStyle}>
                    <label htmlFor="lieuNaissance" style={labelStyle}>Lieu de naissance de la victime</label>
                    <Input name="lieuNaissance" type="text" value={victime?.lieuNaissance || ""} onChange={handleChange} placeholder="Lieu de naissance de la victime" disabled={toUpdate} />
                </div>

                {/* Téléphone */}
                <div style={fieldContainerStyle}>
                    <label htmlFor="telephone" style={labelStyle}>Numéro de téléphone de la victime</label>
                    <Input name="telephone" type="number" value={victime?.telephone || ""} onChange={handleChange} placeholder="Numéro de téléphone de la victime" disabled={toUpdate} />
                </div>

                {/* Adresse */}
                <div style={fieldContainerStyle}>
                    <label htmlFor="adresse" style={labelStyle}>Adresse de la victime</label>
                    <Input name="adresse" value={victime?.adresse || ""} onChange={handleChange} placeholder="Adresse de la victime" disabled={toUpdate} />
                </div>

                {/* Profession */}
                <div style={fieldContainerStyle}>
                    <label htmlFor="profession" style={labelStyle}>Profession de la victime</label>
                    <Select
                        id="profession"
                        showSearch
                        placeholder="Sélectionnez la profession"
                        optionFilterProp="label"
                        value={victime?.profession || ""}
                        onChange={(value) => handleChange({ target: { name: 'profession', id: 'profession', value } })}
                        filterOption={(input, option) => option?.label?.toLowerCase?.().includes(input.toLowerCase())}
                        style={{ width: '100%' }}
                        disabled={toUpdate}
                        options={professions}
                    />
                </div>

                {/* Adresse employeur */}
                <div style={fieldContainerStyle}>
                    <label htmlFor="adresseEmployeur" style={labelStyle}>Adresse de l'employeur de la victime</label>
                    <Input name="adresseEmployeur" value={victime?.adresseEmployeur || ""} onChange={handleChange} disabled={toUpdate} />
                </div>

                {/* Pays de résidence */}
                <div style={fieldContainerStyle}>
                    <label htmlFor="paysResidenceHabituel" style={labelStyle}>Pays de résidence habituel de la victime</label>
                    <Select
                        showSearch
                        name="paysResidenceHabituel"
                        value={victime?.paysResidenceHabituel || ""}
                        onChange={(e) => handleChange(e, "paysResidenceHabituel")}
                        options={pays.map(c => ({ label: c, value: c }))}
                        style={{ width: "100%" }}
                        disabled={toUpdate}
                    />
                </div>

                {/* Revenu avant accident */}
                <div style={fieldContainerStyle}>
                    <label htmlFor="revenuProfessionelMensuelAvantAccident" style={labelStyle}>
                        Mont. du rev. mens. av. l'accident (FCFA)
                    </label>
                    <Input name="revenuProfessionelMensuelAvantAccident" value={victime?.revenuProfessionelMensuelAvantAccident || ""} onChange={handleChange} type="number" disabled={toUpdate} />
                </div>

                {/* Perte de revenu après accident */}
                <div style={fieldContainerStyle}>
                    <label htmlFor="perteRevenuProfessionelMensuelApresAccident" style={labelStyle}>
                        Montant de la perte de revenu mensuel après l'accident (FCFA)
                    </label>
                    <Input name="perteRevenuProfessionelMensuelApresAccident" value={victime?.perteRevenuProfessionelMensuelApresAccident || ""} onChange={handleChange} type="number" disabled={toUpdate} />
                </div>

                {/* Bourse mensuelle */}
                <div style={fieldContainerStyle}>
                    <label htmlFor="montantBourseMensuelle" style={labelStyle}>Montant Bourse officielle mensuelle (FCFA)</label>
                    <Input name="montantBourseMensuelle" value={victime?.montantBourseMensuelle || 0} onChange={handleChange} type="number" disabled={toUpdate} />
                </div>

                {/* ── Véhicule affecté (facultatif) ────────────────────────────── */}
                {vehicleOptions.length > 0 && (
                    <div style={fieldContainerStyle}>
                        <label htmlFor="vehiculeAffecte" style={labelStyle}>
                            <CarOutlined style={{ marginRight: 6 }} />
                            Véhicule dans lequel se trouvait la victime
                        </label>
                        <Select
                            id="vehiculeAffecte"
                            allowClear
                            placeholder="Sélectionner (facultatif)"
                            value={victime?.vehiculeAffecte || undefined}
                            onChange={(e) => handleChange(e ?? "", "vehiculeAffecte")}
                            options={vehicleOptions}
                            style={{ width: "100%" }}
                            disabled={toUpdate}
                        />
                    </div>
                )}
                {/* ─────────────────────────────────────────────────────────────── */}

                {/* Gravité de la blessure (blessé) */}
                {victime?.casVictime === "BLESSE" && (
                    <div style={fieldContainerStyle}>
                        <label htmlFor="graviteBlessure" style={labelStyle}>Gravité de la blessure</label>
                        <Select
                            name="graviteBlessure"
                            value={victime?.graviteBlessure || ""}
                            onChange={(e) => handleChange(e, "graviteBlessure")}
                            options={[
                                { value: "LEGER", label: "🟠 Blessé léger" },
                                { value: "GRAVE", label: "🔴 Blessé grave" },
                            ]}
                            placeholder="Sélectionner la gravité"
                            style={{ width: "100%" }}
                            disabled={toUpdate}
                        />
                    </div>
                )}

                {/* Description des dégâts (blessé) */}
                {victime?.casVictime === "BLESSE" && (
                    <div style={{ ...fieldContainerStyle, gridColumn: "span 2" }}>
                        <label htmlFor="descriptionDegatsVictime" style={labelStyle}>Description des dégâts sur la victime</label>
                        <TextArea name="descriptionDegatsVictime" value={victime?.descriptionDegatsVictime || ""} onChange={handleChange} disabled={toUpdate} />
                    </div>
                )}

                {/* Type d'orphelin (décédé) */}
                {victime?.casVictime === "DECEDE" && (
                    <div style={{ ...fieldContainerStyle, gridColumn: "span 2" }}>
                        <label htmlFor="typeOrphelin" style={labelStyle}>Type d'orphelin(s) laissé(s)</label>
                        <Select
                            name="typeOrphelin"
                            value={victime?.typeOrphelin || ""}
                            onChange={(e) => handleChange(e, "typeOrphelin")}
                            options={[
                                { value: "ORPHELIN SIMPLE", label: "ORPHELIN SIMPLE" },
                                { value: "ORPHELIN DOUBLE", label: "ORPHELIN DOUBLE" },
                            ]}
                            style={{ width: "100%" }}
                            disabled={toUpdate}
                        />
                    </div>
                )}

            </div>
        </Modal>
    );
};

AddVictime.propTypes = {
    open: PropTypes.bool,
    victime: PropTypes.object,
    edit: PropTypes.bool,
    setOpen: PropTypes.func,
    onSaved: PropTypes.func,
    sinistre: PropTypes.object,
    /* ── Véhicules (l'un ou l'autre suffit) ── */
    vehicleOptions: PropTypes.array,   // tableau pré-construit
    contrat: PropTypes.object,         // ou contrat + adversaires pour construction auto
    adversaires: PropTypes.array,
};

AddVictime.defaultProps = {
    adversaires: [],
};

export default AddVictime;